import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  Stack,
} from '@mui/material';
import {
  ExpandMore,
  Add as AddIcon,
  List as ListIcon,
  Edit as EditIcon,
  AccountBalance,
  ExpandLess,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { chartOfAccountService } from '../../services/chartOfAccount.service';
import {
  ChartOfAccount,
  getAccountTypeLabel,
} from '../../types/chartOfAccount';

const ChartOfAccountsTree: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});

  // Translations
  const t = {
    title: isArabic ? 'دليل الحسابات - عرض شجري' : 'Chart of Accounts - Tree View',
    listView: isArabic ? 'عرض قائمة' : 'List View',
    newAccount: isArabic ? 'حساب جديد' : 'New Account',
    noAccounts: isArabic ? 'لا توجد حسابات' : 'No accounts found',
    expandAll: isArabic ? 'توسيع الكل' : 'Expand All',
    collapseAll: isArabic ? 'طي الكل' : 'Collapse All',
    edit: isArabic ? 'تعديل' : 'Edit',
    final: isArabic ? 'نهائي' : 'Final',
    main: isArabic ? 'رئيسي' : 'Main',
    balance: isArabic ? 'الرصيد' : 'Balance',
    kwd: isArabic ? 'د.ك' : 'KWD',
  };

  useEffect(() => {
    loadTree();
  }, []);

  const loadTree = async () => {
    try {
      setLoading(true);
      const data = await chartOfAccountService.getTree();
      setAccounts(data);
      // Expand all by default
      const expandedState: { [key: number]: boolean } = {};
      expandAll(data, expandedState);
      setExpanded(expandedState);
    } catch (err: any) {
      setError(err.response?.data?.message || (isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  const expandAll = (nodes: ChartOfAccount[], expandedState: { [key: number]: boolean }) => {
    nodes.forEach((node) => {
      expandedState[node.accountId] = true;
      if (node.childAccounts && node.childAccounts.length > 0) {
        expandAll(node.childAccounts, expandedState);
      }
    });
  };

  const handleToggle = (accountId: number) => {
    setExpanded((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const formatBalance = (value: number): string => {
    return new Intl.NumberFormat(isArabic ? 'ar-KW' : 'en-GB', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  const renderTree = (node: ChartOfAccount, level: number = 0) => (
    <React.Fragment key={node.accountId}>
      <ListItem 
        disablePadding
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemButton
          sx={{
            pl: level * 4 + 2,
            py: 1.5,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              {node.hasChildren && (
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(node.accountId);
                  }}
                  sx={{
                    color: 'primary.main',
                  }}
                >
                  {expanded[node.accountId] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
              {!node.hasChildren && <Box width={40} />}
              
              <AccountBalance
                fontSize="small"
                sx={{
                  color: node.isFinalAccount ? 'primary.main' : 'text.secondary',
                }}
              />
              
              <Box flex={1}>
                <Typography 
                  variant="body2" 
                  fontWeight={node.isMainAccount ? 'bold' : 'normal'}
                  color={node.isMainAccount ? 'primary.main' : 'text.primary'}
                >
                  {node.accountCode} - {isArabic ? node.accountNameAr : node.accountName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isArabic ? node.accountName : node.accountNameAr}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Chip
                  label={getAccountTypeLabel(node.accountType, language)}
                  size="small"
                  variant="outlined"
                  color={
                    node.accountType === 'Asset'
                      ? 'primary'
                      : node.accountType === 'Liability'
                      ? 'error'
                      : node.accountType === 'Revenue'
                      ? 'success'
                      : node.accountType === 'Equity'
                      ? 'warning'
                      : 'default'
                  }
                  sx={{ fontWeight: 'bold' }}
                />
                {node.isFinalAccount && (
                  <Chip 
                    label={t.final} 
                    size="small" 
                    color="success" 
                    variant="outlined" 
                  />
                )}
                {node.isMainAccount && (
                  <Chip 
                    label={t.main} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                  />
                )}
              </Stack>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <Box minWidth={150} textAlign="right">
                <Typography variant="caption" color="text.secondary">
                  {t.balance}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  {formatBalance(node.currentBalance)} {t.kwd}
                </Typography>
              </Box>
              <Tooltip title={t.edit}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/chart-of-accounts/edit/${node.accountId}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
      {node.childAccounts && node.childAccounts.length > 0 && (
        <Collapse in={expanded[node.accountId]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.childAccounts.map((child) => renderTree(child, level + 1))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
          {t.title}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ListIcon />}
            onClick={() => navigate('/chart-of-accounts')}
          >
            {t.listView}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/chart-of-accounts/create')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            {t.newAccount}
          </Button>
        </Stack>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tree */}
      <Paper sx={{ mb: 3, boxShadow: 3 }}>
        {accounts.length === 0 ? (
          <Box py={8}>
            <Typography color="text.secondary" align="center">
              {t.noAccounts}
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: '100%' }}>
            {accounts.map((node) => renderTree(node, 0))}
          </List>
        )}
      </Paper>

      {/* Expand/Collapse All */}
      <Stack direction="row" spacing={2}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            const expandedState: { [key: number]: boolean } = {};
            expandAll(accounts, expandedState);
            setExpanded(expandedState);
          }}
        >
          {t.expandAll}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setExpanded({})}
        >
          {t.collapseAll}
        </Button>
      </Stack>
    </Box>
  );
};

export default ChartOfAccountsTree;